import './StoriesBriefing.css';
import { SourceCards, type SourceItem } from './SourceCards';

// The real news-briefing UI — renders the structured `stories[]` digest the
// backend streams (brew.stories) into the layout from the preview: direct answer
// → ranked stories (headline, summary, why-it-matters, per-story sources + date)
// → quick summary → source cards → confidence. Frontend owns 100% of the look;
// the model only supplied the data.

interface Story {
  headline: string;
  summary: string;
  why_it_matters?: string;
  sources: number[];
  published_at?: string;
  category?: string;
  /** 2–4 scannable facts. Server-capped at 4. */
  key_points?: string[];
  /** The model's read of the sources, never inferred here. */
  status?: 'confirmed' | 'developing';
}
export interface StoryDigest {
  answer?: string;
  stories: Story[];
}

/** When the briefing was produced, in the READER's timezone.
 *
 *  A news answer without a timestamp asks the reader to trust that "today" means
 *  today. Rendered client-side on purpose: the browser is the only place that
 *  knows the user's actual zone, and a server-rendered time in IST is misleading
 *  to everyone outside it. */
function updatedStamp(ts: number): { text: string; iso: string } {
  const d = new Date(ts);
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const time = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(d);
    const date = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(d);
    return { text: `${date}, ${time}${zone ? '' : ''}`, iso: d.toISOString() };
  } catch {
    return { text: d.toUTCString(), iso: d.toISOString() };
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function newsDate(iso?: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const day = (ms: number) => { const d = new Date(ms); return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()); };
  const diff = Math.round((day(Date.now()) - day(t)) / 86_400_000);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

const CONF: Record<string, { dot: string; label: string }> = {
  high: { dot: 'var(--brief-good, #3fa66a)', label: 'High confidence — the lead stories are confirmed by multiple independent outlets.' },
  medium: { dot: 'var(--brief-warn, #d99a2b)', label: 'Medium confidence — parts rest on a single source.' },
  low: { dot: 'var(--brief-low, #cc5b52)', label: 'Low confidence — limited corroboration in the retrieved sources.' },
};

export function StoriesBriefing({ digest, sources, confidence, updatedAt }: {
  digest: StoryDigest;
  sources: SourceItem[];
  confidence?: 'high' | 'medium' | 'low';
  /** When the briefing was generated (epoch ms, from the brew.stories event). */
  updatedAt?: number;
}) {
  // Render when there's EITHER stories or at least a direct answer — the latter is
  // the guarded fallback (e.g. "I searched but couldn't find reporting on X").
  if (!digest || (!digest.answer && !digest.stories?.length)) return null;
  const byN = new Map(sources.map((s) => [s.n, s]));
  // Importance order — the story the most sources corroborate first.
  const stories = [...(digest.stories ?? [])].sort((a, b) => (b.sources?.length ?? 0) - (a.sources?.length ?? 0));
  const conf = confidence ? CONF[confidence] : undefined;

  const stamp = updatedAt ? updatedStamp(updatedAt) : null;

  return (
    <div className="brief">
      {/* Freshness, stated rather than implied. The reader is being told what is
          happening "today", so the moment that claim was made must be visible. */}
      {stamp ? (
        <div className="brief-head">
          <span className="brief-head__label">Briefing</span>
          <time className="brief-head__time" dateTime={stamp.iso}>Updated {stamp.text}</time>
        </div>
      ) : null}

      {digest.answer ? <p className="brief__lead">{digest.answer}</p> : null}

      {stories.length ? <div className="brief__label">Top Stories</div> : null}
      {stories.map((story, i) => {
        const outlets = [...new Set(story.sources.map((n) => byN.get(n)?.domain).filter(Boolean))];
        const date = newsDate(story.published_at) || newsDate(byN.get(story.sources[0]!)?.publishedAt);
        return (
          <article className="brief-story" key={`${i}-${story.headline}`}>
            {/* Category and status sit ABOVE the headline: both answer "should I
                read this?", which the reader decides before the headline itself.
                Status carries its own word, never colour alone. */}
            {story.category || story.status ? (
              <div className="brief-story__meta">
                {story.category ? <span className="brief-tag">{story.category}</span> : null}
                {story.status ? (
                  <span className={`brief-status brief-status--${story.status}`}>
                    {story.status === 'developing' ? 'Developing' : 'Confirmed'}
                  </span>
                ) : null}
              </div>
            ) : null}
            <h3 className="brief-story__h">
              <span className="brief-story__rank">{i + 1}.</span>
              {story.headline}
              {story.sources.map((n) => (
                <a
                  key={n}
                  className="brief-cite"
                  href={byN.get(n)?.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Source ${n}${byN.get(n)?.domain ? `: ${byN.get(n)?.domain}` : ''}`}
                >{n}</a>
              ))}
            </h3>
            <p className="brief-story__sum">{story.summary}</p>
            {story.key_points?.length ? (
              <ul className="brief-story__points">
                {story.key_points.map((p, k) => <li key={k}>{p}</li>)}
              </ul>
            ) : null}
            {story.why_it_matters ? (
              <p className="brief-story__why"><strong>Why it matters:</strong> {story.why_it_matters}</p>
            ) : null}
            <div className="brief-story__attr">
              {outlets.length ? <span>Sources: {outlets.join(', ')}</span> : null}
              {date ? <><span className="brief-dot">·</span><span>{date}</span></> : null}
            </div>
          </article>
        );
      })}

      {stories.length >= 2 ? (
        <div className="brief-summary">
          <div className="brief__label brief__label--tight">Quick Summary</div>
          <ul>{stories.slice(0, 3).map((s, i) => <li key={i}>{s.headline}</li>)}</ul>
        </div>
      ) : null}

      <SourceCards sources={sources} />

      {conf ? (
        <div className="brief-conf">
          <span className="brief-conf__dot" style={{ background: conf.dot }} />
          {conf.label}
        </div>
      ) : null}
    </div>
  );
}
